import React from "react";
import styled from "styled-components";
import useCheckpointStatus from "../../hooks/useCheckpointStatus";
import { TextSecondary } from "../styled/text";
import { Inter_12_500, Inter_12_600 } from "../../styles/text";
import { ReactComponent as CheckIcon } from "../icons/check.svg";
import { ReactComponent as CrossIcon } from "../icons/cross.svg";
import { ReactComponent as TimerIcon } from "../icons/timer.svg";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Note = styled.div`
  ${Inter_12_500};
  color: ${(p) => p.theme.fontTertiary};
  margin-top: 2px;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  ${Inter_12_600};
  width: fit-content;

  &.verified {
    background-color: #f6ffed;
    color: #52c41a;
  }
  &.pending {
    background-color: #fff7e6;
    color: #fa8c16;
  }
  &.not-verified {
    background-color: #fff1f0;
    color: #f5222d;
  }

  svg {
    width: 12px;
    height: 12px;
    fill: currentColor;
  }
`;

export default function CheckpointVerificationStatus({ blockHeight }) {
  const { status, loading, checkpointBlock } = useCheckpointStatus(blockHeight);

  if (loading) {
    return <TextSecondary>Loading...</TextSecondary>;
  }

  if (!status) {
    const nextCheckpoint = Math.ceil(blockHeight / 10) * 10;
    return (
      <Wrapper>
        <TextSecondary>Not yet verified</TextSecondary>
        <Note>Next checkpoint at block #{nextCheckpoint}</Note>
      </Wrapper>
    );
  }

  // Status mapping:
  // - "accepted" or "final" = verified by droppBCB
  // - "emitted" = checkpoint created, awaiting droppBCB validation
  // - "rejected" = rejected by droppBCB
  const isVerified = status === "accepted" || status === "final";
  const isPending = status === "emitted";
  const isRejected = status === "rejected";

  // Show which blocks are covered by this checkpoint
  const startBlock = checkpointBlock - 9;
  const endBlock = checkpointBlock;

  let badgeClass, icon, label;
  if (isVerified) {
    badgeClass = "verified";
    icon = <CheckIcon />;
    label = "Verified";
  } else if (isPending) {
    badgeClass = "pending";
    icon = <TimerIcon />;
    label = "Pending";
  } else if (isRejected) {
    badgeClass = "not-verified";
    icon = <CrossIcon />;
    label = "Rejected";
  }

  return (
    <Wrapper>
      <Badge className={badgeClass}>
        {icon}
        {label}
      </Badge>
      <Note>
        Blocks #{startBlock.toLocaleString()} - #{endBlock.toLocaleString()}{" "}
        covered by checkpoint #{checkpointBlock.toLocaleString()}
      </Note>
    </Wrapper>
  );
}
